// TODO(LNS-183): Implement scroll-lock
import styled from '@emotion/styled';
import FocusTrap from 'focus-trap-react';

import React, { type PropsWithChildren, useEffect, useRef } from 'react';

import usePreventScroll from '../../hooks/use-prevent-scroll';
import type { LensDialogProps } from '../../types';
import {
  getColorValue,
  getPlacement,
  getShadow,
  getSize,
  getSizeValue,
  u,
} from '../../utilities';
import Backdrop from '../backdrop/backdrop';
import Container from '../container/container';
import { SvgClose } from '../icon/available-icons/close';
import IconButton from '../icon-button/icon-button';

import Spacer from '../spacer/spacer';
import Text from '../text/text';

const DEFAULT_MODAL_CARD_HEIGHT = '70vh';

const ContentWrapper = styled.div<ContentWrapperProps>`
  display: grid;
  grid-template-rows: ${props => props.rows};
  position: relative;
`;

const ModalCardWrapper = styled.dialog<ModalCardProps>`
  top: ${props =>
    getPlacement(getSizeValue(props.maxHeight), props.placement).top};
  background-color: ${getColorValue('overlay')};
  color: ${getColorValue('body')};
  bottom: ${props => getPlacement(props.maxHeight, props.placement).bottom};
  ${getShadow('large')};
  border-radius: var(--ds-radius-xlarge);
  // Unsets bottom-radius for bottom-aligned modals
  border-bottom-left-radius: ${props =>
    props.placement === 'bottom' ? 'initial' : undefined};
  border-bottom-right-radius: ${props =>
    props.placement === 'bottom' ? 'initial' : undefined};
  ${props => getSize('max-height', props.maxHeight)};
  ${props => getSize('max-width', props.maxWidth)};
  margin: 0 auto;
  position: ${props => getPlacement(props.maxHeight, props.placement).position};
  overflow: auto;
  width: 100%;
  // TODO: LNS-150: Bake dialog resets into native resets file
  border: 0;
  padding: 0;
  &::backdrop {
    background: var(--lns-color-overlay);
  }
`;

const CloseIconSection = styled.div`
  position: absolute;
  top: ${u(1.5)};
  right: ${u(1.5)};
  z-index: 1;
`;

const RightButtonsSection = styled.div`
  margin-left: auto;

  * {
    vertical-align: middle;
  }
`;

const TitleSection = styled.div<TitleSectionProps>`
  padding-left: var(--lns-space-xlarge);
  padding-right: var(--lns-space-xlarge);
  padding-top: var(--lns-space-xlarge);
  padding-bottom: ${props => props.bottom};
  flex-shrink: 0;
`;

const ButtonsSection = styled.div<ButtonsSectionProps>`
  padding-left: var(--lns-space-xlarge);
  padding-right: var(--lns-space-xlarge);
  padding-bottom: var(--lns-space-xlarge);
  padding-top: ${props =>
    props.hasDividers ? 'var(--lns-space-medium)' : props.top};
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChildrenSection = styled.div<Omit<ChildrenSectionProps, 'title'>>`
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding-top: ${props =>
    props.hasTitle && !props.noPadding
      ? 0
      : !props.hasTitle && !props.noPadding
        ? 'var(--lns-space-xlarge)'
        : 0};
  padding-bottom: ${props =>
    props.hasButtons && !props.noPadding
      ? 0
      : !props.hasButtons && !props.noPadding
        ? 'var(--lns-space-xlarge)'
        : 0};
  padding-left: ${props => (props.noPadding ? 0 : 'var(--lns-space-xlarge)')};
  padding-right: ${props => (props.noPadding ? 0 : 'var(--lns-space-xlarge)')};
  border-style: solid;
  border-color: ${getColorValue('border')};
  border-width: ${props => (props.hasDividers ? '1px 0' : '0')};
`;

const ModalCardChildrenSection = styled.div<{ maxHeight: number | string }>`
  overflow: auto;

  ${props => getMaxHeight(props.maxHeight)};

  & > * {
    ${props => getMaxHeight(props.maxHeight)};
  }
`;

const getMaxHeight = (maxHeight: number | string) => {
  if (typeof maxHeight === 'number') {
    return getSize('max-height', maxHeight);
  }

  return 'max-height: ' + maxHeight;
};

export const ModalCard = ({
  children,
  onCloseClick,
  isOpen,
  maxWidth = 60,
  maxHeight = DEFAULT_MODAL_CARD_HEIGHT,
  placement = 'center',
  ariaLabel,
  ariaModal,
  ariaLabelledBy,
  ref,
  removeClose,
  initialFocus,
  closeOnEscape = true,
  ...props
}: ModalCardProps & {
  initialFocus?:
    | HTMLElement
    | SVGElement
    | string
    | false
    | (() => HTMLElement | SVGElement | string | false | undefined);
} & React.ComponentProps<typeof ModalCardWrapper> &
  Omit<
    LensDialogProps,
    keyof ModalCardProps & React.ComponentProps<typeof ModalCardWrapper>
  >) => {
  // Keep a local handle on the dialog element so the focus trap always has a
  // guaranteed fallback target. Lazy-loaded children may not have mounted a
  // tabbable node yet when `isOpen` flips to true, and focus-trap throws if it
  // activates with an empty container.
  const dialogRef = useRef<HTMLDialogElement>(null);
  const setDialogRef = (node: HTMLDialogElement | null) => {
    dialogRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLDialogElement | null>).current = node;
    }
  };

  const keyListener = e => {
    if (e.key === 'Escape' && closeOnEscape) {
      e.preventDefault();

      if (!removeClose) {
        onCloseClick(e);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyListener);

    return () => {
      window.removeEventListener('keydown', keyListener);
    };
  }, [isOpen, onCloseClick]);

  usePreventScroll('html', isOpen);

  return (
    <FocusTrap
      active={isOpen}
      focusTrapOptions={{
        clickOutsideDeactivates: false,
        allowOutsideClick: true,
        // The dialog element is always rendered, so it is a safe focus target
        // when no tabbable content exists yet (e.g. lazy-loaded children still
        // resolving, or `removeClose` modals with no interactive elements).
        fallbackFocus: () => dialogRef.current as HTMLElement,
        ...(initialFocus !== undefined ? { initialFocus } : {}),
      }}
    >
      {/* This dialog is interactable with the keyboard and need keyboard events -
        the keydown event is handled by the useEffect above.
        */}
      {/* eslint-disable-next-line styled-components-a11y/no-noninteractive-element-interactions, styled-components-a11y/click-events-have-key-events */}
      <ModalCardWrapper
        open={isOpen}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        placement={placement}
        onClick={e => e.stopPropagation()}
        ref={setDialogRef}
        aria-label={ariaLabel}
        aria-modal={ariaModal}
        aria-labelledby={ariaLabelledBy}
        {...props}
      >
        {!removeClose && onCloseClick && (
          <CloseIconSection>
            <IconButton
              altText="Close"
              icon={<SvgClose />}
              size="small"
              onClick={onCloseClick}
            />
          </CloseIconSection>
        )}
        <ModalCardChildrenSection maxHeight={maxHeight}>
          {children}
        </ModalCardChildrenSection>
      </ModalCardWrapper>
    </FocusTrap>
  );
};

type AllProps = ModalProps & React.ComponentProps<typeof Backdrop>;

const Modal = React.forwardRef(
  (
    {
      children,
      id,
      isOpen,
      mainButton,
      secondaryButton,
      alternativeButton,
      title,
      noPadding,
      onCloseClick,
      onBackgroundClick,
      onKeyDown,
      hasDividers,
      maxHeight = '70vh',
      maxWidth = 60,
      placement = 'center',
      zIndex = 1000,
      ariaLabel,
      ariaModal = true,
      ariaLabelledBy,
      initialFocus,
      ...props
    }: AllProps & {
      initialFocus?:
        | HTMLElement
        | SVGElement
        | string
        | false
        | (() => HTMLElement | SVGElement | string | false | undefined);
    },
    ref: React.ForwardedRef<HTMLDialogElement>
  ) => {
    const backdropRef = useRef<HTMLDivElement>(null);
    const titleId = id ? `${id}-modal-title` : 'modal-title';
    const hasButtons =
      mainButton || secondaryButton || alternativeButton ? true : false;
    const closeClickHandler = e => {
      if (onBackgroundClick) {
        e.stopPropagation();
        onBackgroundClick(e);

        return;
      }

      onCloseClick(e);
    };

    // Hide all non-modal content from screen readers when modal is open
    useEffect(() => {
      if (!isOpen || !backdropRef.current) {
        return;
      }

      const container = backdropRef.current.parentElement;

      if (!container) {
        return;
      }

      // Hide all children of container except the modal backdrop
      const siblings = Array.from(container.children).filter(
        child => child !== backdropRef.current && child instanceof HTMLElement
      ) as HTMLElement[];

      siblings.forEach(sibling => {
        if (!sibling.hasAttribute('aria-hidden')) {
          sibling.setAttribute('aria-hidden', 'true');
          sibling.setAttribute('data-lens-modal-hidden', 'true');
        }
      });

      return () => {
        // Remove aria-hidden from all elements we marked
        const hiddenElements = document.querySelectorAll(
          '[data-lens-modal-hidden]'
        );
        hiddenElements.forEach(element => {
          element.removeAttribute('aria-hidden');
          element.removeAttribute('data-lens-modal-hidden');
        });
      };
    }, [isOpen]);

    return (
      <Backdrop ref={backdropRef} isOpen={isOpen} zIndex={zIndex} {...props}>
        <Container
          height="100%"
          onClick={closeClickHandler}
          onKeyDown={onKeyDown}
        >
          <ModalCard
            ref={ref}
            id={id}
            isOpen={isOpen}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
            placement={placement}
            onCloseClick={onCloseClick}
            ariaLabel={ariaLabel}
            ariaModal={ariaModal}
            ariaLabelledBy={ariaLabelledBy ?? titleId ?? undefined}
            initialFocus={initialFocus}
          >
            <ContentWrapper
              rows={`${title ? 'auto ' : ''} ${children ? '1fr ' : ''} ${
                hasButtons ? 'auto' : ''
              }`}
            >
              {title && (
                <TitleSection
                  bottom={
                    children
                      ? 'var(--lns-space-medium)'
                      : 'var(--lns-space-xlarge)'
                  }
                >
                  <Text htmlTag="h1" variant="title" id={titleId}>
                    {title}
                  </Text>
                </TitleSection>
              )}
              <ChildrenSection
                noPadding={noPadding}
                hasDividers={hasDividers}
                hasTitle={Boolean(title)}
                hasButtons={hasButtons}
              >
                {children && children}
              </ChildrenSection>

              {hasButtons && (
                <ButtonsSection
                  top={children ? 'var(--lns-space-xlarge)' : 0}
                  hasDividers={hasDividers}
                >
                  {alternativeButton}
                  <RightButtonsSection>
                    {secondaryButton && (
                      <Spacer right="small" isInline>
                        {secondaryButton}
                      </Spacer>
                    )}
                    {mainButton}
                  </RightButtonsSection>
                </ButtonsSection>
              )}
            </ContentWrapper>
          </ModalCard>
        </Container>
      </Backdrop>
    );
  }
);

type ModalProps = PropsWithChildren<{
  isOpen?: boolean;
  mainButton?: React.ReactNode;
  secondaryButton?: React.ReactNode;
  alternativeButton?: React.ReactNode;
  title?: React.ReactNode;
  onCloseClick?: React.ReactEventHandler;
  onBackgroundClick?: React.ReactEventHandler;
  onKeyDown?: React.ReactEventHandler;
  hasDividers?: boolean;
  maxHeight?: number | string;
  maxWidth?: number | string;
  noPadding?: boolean;
  placement?: 'center' | 'bottom';
  zIndex?: number;
  ariaLabel?: string;
  ariaModal?: boolean;
  ariaLabelledBy?: string;
  initialFocus?:
    | HTMLElement
    | SVGElement
    | string
    | false
    | (() => HTMLElement | SVGElement | string | false | undefined);
}>;

type ModalCardProps = PropsWithChildren<{
  id?: string;
  isOpen?: boolean;
  onKeyDown?: React.ReactEventHandler;
  onCloseClick?: React.ReactEventHandler;
  closeOnEscape?: boolean;
  placement?: string;
  maxHeight?: number | string;
  maxWidth?: number | string;
  ariaLabel?: string;
  ariaModal?: boolean;
  ariaLabelledBy?: string;
  removeClose?: boolean;
}>;

type ChildrenSectionProps = {
  title?: React.ReactNode;
  hasButtons?: boolean;
  hasDividers?: boolean;
  hasTitle: boolean;
  noPadding?: boolean;
};

type ButtonsSectionProps = {
  top?: string | number;
  hasDividers?: boolean;
};

type TitleSectionProps = {
  bottom: number | string;
};

type ContentWrapperProps = {
  rows: string;
};

export default Modal;
